import { Button } from "@/components/ui/button";
import { Phone, Menu, X, Flame, FileText, Home, ArrowRight, HelpCircle, Users, Briefcase, Shield, Building2, Hammer, Wrench, Package, Building, Compass } from "lucide-react";
import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { useAuth } from "@/contexts/AuthContext";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@/components/ui/navigation-menu";
import { cn } from "@/lib/utils";
import { motion } from "framer-motion";
import CeccaLogo from "./CeccaLogo";

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [menuAnimated, setMenuAnimated] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const { user, isAdmin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Handle scroll effect - transparent header only on homepage
  const isHomepage = location.pathname === "/";
  
  useEffect(() => {
    if (!isHomepage) {
      setIsScrolled(true);
      return;
    }
    
    // Hysteresis prevents flicker around the threshold ("disappearing"/jumping)
    const SCROLL_ON = 80;
    const SCROLL_OFF = 20;
    let rafId: number | null = null;

    const update = () => {
      rafId = null;
      const y = window.scrollY;
      setIsScrolled((prev) => {
        if (!prev && y > SCROLL_ON) return true;
        if (prev && y < SCROLL_OFF) return false;
        return prev;
      });
    };

    const handleScroll = () => {
      if (rafId != null) return;
      rafId = window.requestAnimationFrame(update);
    };

    // Initialize
    update();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", handleScroll);
      if (rafId != null) window.cancelAnimationFrame(rafId);
    };
  }, [isHomepage]);

  const handleFaqClick = (e: React.MouseEvent) => {
    e.preventDefault();
    setMobileMenuOpen(false);
    
    if (location.pathname === "/") {
      // Already on home page, just scroll to FAQ
      const faqElement = document.getElementById("faq");
      if (faqElement) {
        faqElement.scrollIntoView({ behavior: "smooth" });
      }
    } else {
      // Navigate to home page with hash
      navigate("/#faq");
    }
  };

  // Trigger staggered animations when menu opens
  useEffect(() => {
    if (mobileMenuOpen) {
      const timer = setTimeout(() => setMenuAnimated(true), 50);
      return () => clearTimeout(timer);
    } else {
      setMenuAnimated(false);
    }
  }, [mobileMenuOpen]);

  const servicePages = [
    { name: "Brand-, vand-, storm- og sneskader", href: "/brand-vand-storm-sneskader", icon: Flame, description: "Større skader på bygning og inventar" },
    { name: "Forsikringsopgørelser og taksering", href: "/forsikringsopgoerelser", icon: FileText, description: "Korrekt opgørelse og taksering" },
    { name: "Myndighedskontakt", href: "/myndighedskontakt", icon: Building2, description: "Dialog med kommune og myndigheder" },
    { name: "Nedrivning, sanering og genopbygning", href: "/nedrivning-sanering-genopbygning", icon: Hammer, description: "Hele forløbet styret professionelt" },
    { name: "Renovering og ombygning", href: "/renovering-ombygning", icon: Wrench, description: "Uvildig byggerådgivning" },
    { name: "Indbo, inventar, løsøre og driftstab", href: "/indbo-driftstab", icon: Package, description: "Korrekt opgørelse af løsøre og tab" },
    { name: "Byggestyring", href: "/byggestyring", icon: Building, description: "Fagligt tilsyn og styring" },
    { name: "Ejerskifte", href: "/ejerskifteforsikring", icon: Home, description: "Rådgivning ved ejerskiftesager" },
  ];

  const navigation = [
    { name: "Cases", href: "/cases", isLink: true as const },
    { name: "Job & karriere", href: "/job-karriere", isLink: true as const },
    { name: "Samarbejdspartnere", href: "/samarbejdspartnere", isLink: true as const },
  ];

  const aboutPages = [
    { name: "Om os", href: "/om-os", icon: Users, description: "Lær CECCA at kende" },
    { name: "Vores tilgang", href: "/tilgang", icon: Compass, description: "Sådan arbejder vi" },
  ];

  return (
    <header className={cn(
      "fixed top-0 left-0 right-0 z-50 transition-all duration-300",
      // Keep header always within viewport; use translate instead of changing `top`
      !isScrolled && "translate-y-3 sm:translate-y-5",
      isScrolled
        ? "bg-background/95 backdrop-blur-sm border-b border-border shadow-md"
        : "bg-transparent border-b border-transparent"
    )}>
      <nav className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className={cn(
          "flex items-center justify-between transition-all duration-300",
          isScrolled ? "h-14 sm:h-16 lg:h-16" : "h-16 sm:h-18 lg:h-22"
        )}>
          {/* Logo */}
          <div className="flex-shrink-0">
            <a href="/" aria-label="CECCA Rådgivning – gå til forsiden" className="block transition-all duration-300">
              <CeccaLogo 
                className={cn(
                  "transition-all duration-300",
                  isScrolled ? "h-8 sm:h-9 lg:h-10" : "h-9 sm:h-10 lg:h-12"
                )}
                textColor={isScrolled ? "hsl(355 60% 28%)" : "#ffffff"}
              />
            </a>
          </div>

          {/* Desktop Navigation - Centered */}
          <div className="hidden lg:flex lg:items-center lg:justify-center lg:space-x-5 lg:absolute lg:left-1/2 lg:-translate-x-1/2">
            {/* Services Mega Menu */}
            <NavigationMenu>
              <NavigationMenuList>
                <NavigationMenuItem>
                  <NavigationMenuTrigger className={cn(
                    "bg-transparent text-sm font-medium transition-colors h-auto py-2 px-3",
                    isScrolled 
                      ? "text-foreground hover:text-primary hover:bg-muted/50 data-[state=open]:bg-muted/50" 
                      : "text-white hover:text-white/80 hover:bg-white/10 data-[state=open]:bg-white/10"
                  )}>
                    Vores ydelser
                  </NavigationMenuTrigger>
                  <NavigationMenuContent>
                    <ul className="grid w-[600px] gap-1 p-4 md:grid-cols-2">
                      {servicePages.map((item) => {
                        const Icon = item.icon;
                        return (
                          <li key={item.name}>
                            <NavigationMenuLink asChild>
                              <Link
                                to={item.href}
                                className={cn(
                                  "group flex items-start gap-3 rounded-lg p-3 transition-all duration-200",
                                  "hover:bg-accent/10 hover:shadow-sm",
                                  "focus:outline-none focus:ring-2 focus:ring-primary/20"
                                )}
                              >
                                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-colors duration-200">
                                  <Icon className="h-5 w-5" />
                                </div>
                                <div className="flex flex-col">
                                  <span className="text-sm font-semibold text-foreground group-hover:text-primary transition-colors">
                                    {item.name}
                                  </span>
                                  <span className="text-xs text-muted-foreground line-clamp-1">
                                    {item.description}
                                  </span>
                                </div>
                              </Link>
                            </NavigationMenuLink>
                          </li>
                        );
                      })}
                    </ul>
                  </NavigationMenuContent>
                </NavigationMenuItem>
              </NavigationMenuList>
            </NavigationMenu>

            {/* Cases link first */}
            <Link
              to="/cases"
              className={cn(
                "relative text-sm font-medium transition-colors py-2 px-1 group",
                isScrolled ? "text-foreground hover:text-primary" : "text-white hover:text-white/80"
              )}
            >
              Cases
              <span className={cn(
                "absolute bottom-0 left-0 w-full h-0.5 scale-x-0 group-hover:scale-x-100 transition-transform duration-200 origin-left",
                isScrolled ? "bg-primary" : "bg-white"
              )} />
            </Link>

            {/* Om os dropdown */}
            <NavigationMenu>
              <NavigationMenuList>
                <NavigationMenuItem>
                  <NavigationMenuTrigger className={cn(
                    "bg-transparent text-sm font-medium transition-colors h-auto py-2 px-3",
                    isScrolled
                      ? "text-foreground hover:text-primary hover:bg-muted/50 data-[state=open]:bg-muted/50"
                      : "text-white hover:text-white/80 hover:bg-white/10 data-[state=open]:bg-white/10"
                  )}>
                    Om os
                  </NavigationMenuTrigger>
                  <NavigationMenuContent>
                    <ul className="grid w-[320px] gap-1 p-3">
                      {aboutPages.map((item) => {
                        const Icon = item.icon;
                        return (
                          <li key={item.name}>
                            <NavigationMenuLink asChild>
                              <Link
                                to={item.href}
                                className="group flex items-start gap-3 rounded-lg p-3 transition-all duration-200 hover:bg-accent/10 hover:shadow-sm focus:outline-none focus:ring-2 focus:ring-primary/20"
                              >
                                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-colors duration-200">
                                  <Icon className="h-5 w-5" />
                                </div>
                                <div className="flex flex-col">
                                  <span className="text-sm font-semibold text-foreground group-hover:text-primary transition-colors">
                                    {item.name}
                                  </span>
                                  <span className="text-xs text-muted-foreground line-clamp-1">
                                    {item.description}
                                  </span>
                                </div>
                              </Link>
                            </NavigationMenuLink>
                          </li>
                        );
                      })}
                    </ul>
                  </NavigationMenuContent>
                </NavigationMenuItem>
              </NavigationMenuList>
            </NavigationMenu>

            {navigation.filter(n => n.name !== "Cases").map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={cn(
                  "relative text-sm font-medium transition-colors py-2 px-1 group",
                  isScrolled ? "text-foreground hover:text-primary" : "text-white hover:text-white/80"
                )}
              >
                {item.name}
                <span className={cn(
                  "absolute bottom-0 left-0 w-full h-0.5 scale-x-0 group-hover:scale-x-100 transition-transform duration-200 origin-left",
                  isScrolled ? "bg-primary" : "bg-white"
                )} />
              </Link>
            ))}
          </div>

          {/* Contact CTA */}
          <div className="hidden lg:flex lg:items-center lg:space-x-4">
            <a
              href="tel:+4593889029"
              className={cn(
                "flex items-center text-sm font-medium transition-colors group",
                isScrolled ? "text-foreground hover:text-primary" : "text-white hover:text-white/80"
              )}
            >
              <div className={cn(
                "p-1.5 rounded-full transition-colors mr-2",
                isScrolled 
                  ? "bg-primary/10 group-hover:bg-primary group-hover:text-primary-foreground" 
                  : "bg-white/20 group-hover:bg-white/30"
              )}>
                <Phone className="h-3.5 w-3.5" />
              </div>
              +45 93 88 90 29
            </a>
            <Button size="sm" className="bg-accent hover:bg-accent/90 shadow-md hover:shadow-lg transition-all duration-200">
              Kontakt os
            </Button>
          </div>

          {/* Mobile menu button */}
          <div className="lg:hidden flex items-center gap-2">
            {/* Mobile phone quick action - 48px touch target */}
            <a
              href="tel:+4593889029"
              className={cn(
                "flex items-center justify-center h-12 w-12 rounded-xl active:scale-95 transition-all duration-150",
                isScrolled 
                  ? "text-foreground hover:bg-muted active:bg-muted/80" 
                  : "text-white hover:bg-white/10 active:bg-white/20"
              )}
              aria-label="Ring til os"
            >
              <Phone className="h-5 w-5" />
            </a>
            
            <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <SheetTrigger asChild>
                <Button 
                  variant="ghost" 
                  size="icon" 
                  aria-label="Åbn menu"
                  className={cn(
                    "h-12 w-12 rounded-xl active:scale-95 transition-all duration-150",
                    isScrolled 
                      ? "text-foreground hover:bg-muted" 
                      : "text-white hover:bg-white/10"
                  )}
                >
                  <Menu className="h-6 w-6" />
                </Button>
              </SheetTrigger>
              <SheetContent 
                side="top" 
                className="w-full h-[100dvh] max-h-[calc(100dvh-env(safe-area-inset-bottom,0px))] p-0 border-none [&>button]:hidden overflow-hidden"
                style={{
                  background: 'linear-gradient(180deg, hsl(var(--background)) 0%, hsl(220 10% 96%) 100%)'
                }}
              >
                <div className="h-full w-full">

                  {/* Decorative background elements */}
                  <div className="absolute inset-0 overflow-hidden pointer-events-none">
                    <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full bg-primary/5 blur-3xl" />
                    <div className="absolute top-1/3 -left-32 w-96 h-96 rounded-full bg-accent/5 blur-3xl" />
                    <div className="absolute bottom-20 right-10 w-48 h-48 rounded-full bg-primary/3 blur-2xl" />
                  </div>

                  {/* Fullscreen menu header - optimized touch targets */}
                  <div className="relative flex items-center justify-between px-4 sm:px-6 py-4 pt-6 border-b border-border/30">
                    <CeccaLogo className="h-9" textColor="hsl(355 60% 28%)" />
                    <button 
                      onClick={() => setMobileMenuOpen(false)}
                      className="flex items-center justify-center h-12 w-12 min-h-[48px] min-w-[48px] rounded-full bg-muted/80 hover:bg-muted active:bg-muted/60 active:scale-95 text-foreground transition-all duration-150"
                      aria-label="Luk menu"
                    >
                      <X className="h-6 w-6" />
                    </button>
                  </div>
                  
                  {/* Fullscreen navigation - touch optimized */}
                  <div className="relative flex flex-col h-[calc(100dvh-88px-env(safe-area-inset-bottom,0px))]">
                  <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-5 overscroll-contain">
                    {/* Main navigation - all displayed as boxes */}
                    <nav className="space-y-5">
                      {/* Services section */}
                      <div>
                        <h3 
                          className={cn(
                            "text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2.5 transform transition-all duration-500 ease-out",
                            menuAnimated ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0"
                          )}
                          style={{ transitionDelay: '0ms' }}
                        >
                          Services
                        </h3>
                        <div className="grid grid-cols-2 gap-2.5">
                          {servicePages.map((item, index) => {
                            const Icon = item.icon;
                            return (
                              <Link
                                key={item.name}
                                to={item.href}
                                onClick={() => setMobileMenuOpen(false)}
                                className={cn(
                                  "group flex flex-col gap-2 p-3.5 rounded-xl bg-background/80 border border-border/50 hover:border-primary/30 hover:shadow-md active:scale-[0.97] active:bg-muted/50 transition-all duration-150 touch-manipulation",
                                  "transform transition-all duration-500 ease-out",
                                  menuAnimated ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0"
                                )}
                                style={{ transitionDelay: `${(index + 1) * 50}ms` }}
                              >
                                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10 text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-colors duration-200">
                                  <Icon className="h-5 w-5" />
                                </div>
                                <div className="flex flex-col gap-0.5">
                                  <span className="text-sm font-semibold text-foreground group-hover:text-primary transition-colors leading-tight">
                                    {item.name}
                                  </span>
                                  <span className="text-[11px] text-muted-foreground line-clamp-1 leading-relaxed">
                                    {item.description}
                                  </span>
                                </div>
                              </Link>
                            );
                          })}
                        </div>
                      </div>

                      {/* Other navigation as boxes */}
                      <div>
                        <h3 
                          className={cn(
                            "text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2.5 transform transition-all duration-500 ease-out",
                            menuAnimated ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0"
                          )}
                          style={{ transitionDelay: '350ms' }}
                        >
                          Mere
                        </h3>
                        <div className="grid grid-cols-2 gap-2.5">
                          {[
                            { name: "Cases", href: "/cases", isLink: true as const },
                            { name: "Om os", href: "/om-os", isLink: true as const },
                            { name: "Vores tilgang", href: "/tilgang", isLink: true as const },
                            { name: "Job & karriere", href: "/job-karriere", isLink: true as const },
                            { name: "Samarbejdspartnere", href: "/samarbejdspartnere", isLink: true as const },
                          ].map((item, index) => {
                            const navIcons: Record<string, React.ReactNode> = {
                              "Cases": <Briefcase className="h-5 w-5" />,
                              "FAQ": <HelpCircle className="h-5 w-5" />,
                              "Om os": <Users className="h-5 w-5" />,
                              "Vores tilgang": <Compass className="h-5 w-5" />,
                              "Job & karriere": <Briefcase className="h-5 w-5" />,
                              "Samarbejdspartnere": <Users className="h-5 w-5" />
                            };
                            
                            if (item.href === "#faq") {
                              return (
                                <button
                                  key={item.name}
                                  onClick={handleFaqClick}
                                  className={cn(
                                    "group flex flex-col items-center justify-center gap-2 p-3.5 rounded-xl bg-background/80 border border-border/50 hover:border-primary/30 hover:shadow-md active:scale-[0.97] active:bg-muted/50 transition-all duration-150 touch-manipulation",
                                    "transform transition-all duration-500 ease-out",
                                    menuAnimated ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0"
                                  )}
                                  style={{ transitionDelay: `${400 + index * 50}ms` }}
                                >
                                  <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-accent/10 text-accent group-hover:bg-accent group-hover:text-accent-foreground transition-colors duration-200">
                                    {navIcons[item.name]}
                                  </div>
                                  <span className="text-sm font-semibold text-foreground group-hover:text-primary transition-colors">
                                    {item.name}
                                  </span>
                                </button>
                              );
                            }
                            
                            return (
                              <Link
                                key={item.name}
                                to={item.href}
                                onClick={() => setMobileMenuOpen(false)}
                                className={cn(
                                  "group flex flex-col items-center justify-center gap-2 p-3.5 rounded-xl bg-background/80 border border-border/50 hover:border-primary/30 hover:shadow-md active:scale-[0.97] active:bg-muted/50 transition-all duration-150 touch-manipulation",
                                  "transform transition-all duration-500 ease-out",
                                  menuAnimated ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0"
                                )}
                                style={{ transitionDelay: `${400 + index * 50}ms` }}
                              >
                                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-accent/10 text-accent group-hover:bg-accent group-hover:text-accent-foreground transition-colors duration-200">
                                  {navIcons[item.name]}
                                </div>
                                <span className="text-sm font-semibold text-foreground group-hover:text-primary transition-colors">
                                  {item.name}
                                </span>
                              </Link>
                            );
                          })}
                        </div>
                      </div>
                    </nav>
                  </div>
                  
                  {/* Bottom actions - optimized for thumb zone */}
                  <div 
                    className={cn(
                      "relative border-t border-border/30 bg-background/60 backdrop-blur-sm px-4 sm:px-6 py-4 pb-safe space-y-3",
                      "transform transition-all duration-500 ease-out delay-300",
                      menuAnimated ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"
                    )}
                  >
                    {/* Phone CTA - large touch target */}
                    <a
                      href="tel:+4593889029"
                      aria-label="Ring til os på +45 93 88 90 29"
                      className="flex items-center justify-center gap-3 min-h-[56px] py-4 px-6 rounded-2xl bg-muted/80 hover:bg-muted active:bg-muted/60 active:scale-[0.98] text-foreground font-semibold text-lg transition-all duration-150 touch-manipulation"
                    >
                      <div className="flex items-center justify-center h-11 w-11 rounded-full bg-primary/10 text-primary">
                        <Phone className="h-5 w-5" />
                      </div>
                      <span>+45 93 88 90 29</span>
                    </a>

                    {/* Admin button if applicable */}
                    {isAdmin && (
                      <Button 
                        variant="outline" 
                        className="w-full min-h-[56px] h-14 text-base rounded-2xl border-border/50 hover:border-primary/50 active:scale-[0.98] transition-all touch-manipulation" 
                        asChild
                      >
                        <Link to="/admin" onClick={() => setMobileMenuOpen(false)}>
                          <Shield className="h-5 w-5 mr-2" />
                          Admin
                        </Link>
                      </Button>
                    )}

                    {/* Primary CTA - prominent touch target */}
                    <Button 
                      className="w-full min-h-[56px] h-14 text-base font-semibold rounded-2xl bg-primary hover:bg-primary/90 active:scale-[0.98] shadow-lg hover:shadow-xl transition-all duration-150 touch-manipulation"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      Kontakt os
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </Button>
                  </div>
                </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </nav>
    </header>
  );
};

export default Header;
